import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  BarChart3, Scale, TrendingUp, FileText, Shield, 
  GraduationCap, Building2, Users, AlertTriangle, Calculator 
} from 'lucide-react';

const SERVICES = [
  { slug: 'asesoria-financiera', title: 'Asesoría Financiera', desc: 'Toma el control de tus finanzas con un plan personalizado.', icon: TrendingUp, color: '#D4AF37' },
  { slug: 'asesoria-tributaria', title: 'Asesoría Tributaria', desc: 'Tus impuestos sin estrés, con respaldo profesional.', icon: FileText, color: '#4A90D9' },
  { slug: 'asesoria-legal', title: 'Asesoría Legal', desc: 'Protección jurídica integral para tu tranquilidad.', icon: Scale, color: '#7B68EE' },
  { slug: 'fondo-de-inversion', title: 'Fondo de Inversión', desc: 'Haz que tu dinero trabaje por ti.', icon: BarChart3, color: '#2ECC71' },
  { slug: 'gestion-contable', title: 'Gestión Contable', desc: 'Tus números en orden, tu negocio en control.', icon: Calculator, color: '#E67E22' },
  { slug: 'auditoria', title: 'Auditoría', desc: 'Transparencia y confianza en cada cifra.', icon: Shield, color: '#E74C3C' },
  { slug: 'planeacion-financiera', title: 'Planeación Financiera', desc: 'Un mapa claro hacia tus metas financieras.', icon: TrendingUp, color: '#1ABC9C' },
  { slug: 'consultoria-empresarial', title: 'Consultoría Empresarial', desc: 'Impulsa tu empresa al siguiente nivel.', icon: Building2, color: '#9B59B6' },
  { slug: 'gestion-de-riesgos', title: 'Gestión de Riesgos', desc: 'Anticípate a lo inesperado.', icon: AlertTriangle, color: '#F39C12' },
  { slug: 'capacitacion-financiera', title: 'Capacitación Financiera', desc: 'El conocimiento financiero es poder.', icon: GraduationCap, color: '#16A085' },
];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * ServiceAds - Shows random LD Holdings service cards for free-tier users
 * @param {string} variant - "sidebar" (desktop) | "banner" (mobile header)
 * @param {number} count - Number of ads to show
 */
const ServiceAds = ({ variant = 'sidebar', count = 3 }) => {
  const [ads, setAds] = useState([]);

  useEffect(() => {
    setAds(shuffle(SERVICES).slice(0, count));
  }, [count]);

  if (!ads.length) return null;

  if (variant === 'banner') {
    // Horizontal banner for mobile - single ad
    const ad = ads[0];
    const Icon = ad.icon;
    return (
      <div className="mb-4" data-testid="service-ad-banner">
        <Link 
          to={`/servicios/${ad.slug}`}
          className="flex items-center gap-3 p-3 rounded-xl border transition-all hover:scale-[1.01]"
          style={{ 
            borderColor: `${ad.color}30`, 
            background: `linear-gradient(135deg, ${ad.color}08, transparent)` 
          }}
        >
          <div className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${ad.color}15` }}>
            <Icon size={18} style={{ color: ad.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-white truncate">{ad.title}</p>
            <p className="text-[10px] text-gray-400 truncate">{ad.desc}</p>
          </div>
          <span className="text-[9px] text-gray-600 uppercase tracking-wider shrink-0">AD</span>
        </Link>
      </div>
    );
  }

  // Sidebar variant for desktop
  return (
    <div className="space-y-3" data-testid="service-ad-sidebar">
      <p className="text-[10px] uppercase tracking-[0.15em] text-gray-600 font-medium px-1">Servicios LD Holdings</p>
      {ads.map((ad) => {
        const Icon = ad.icon;
        return (
          <Link
            key={ad.slug}
            to={`/servicios/${ad.slug}`}
            className="block p-3 rounded-xl border transition-all hover:scale-[1.02] group"
            style={{ 
              borderColor: `${ad.color}20`, 
              background: `linear-gradient(135deg, ${ad.color}05, transparent)` 
            }}
            data-testid={`service-ad-${ad.slug}`}
          >
            <div className="flex items-center gap-2.5 mb-1.5">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${ad.color}15` }}>
                <Icon size={14} style={{ color: ad.color }} />
              </div>
              <p className="text-xs font-semibold text-white group-hover:text-[#D4AF37] transition-colors">{ad.title}</p>
            </div>
            <p className="text-[11px] text-gray-400 leading-relaxed pl-[38px]">{ad.desc}</p>
          </Link>
        );
      })}
    </div>
  );
};

export default ServiceAds;
